import { faEye, faEyeLowVision } from "@elynx/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ExpandMore } from "@mui/icons-material";
import { Box, Divider, IconButton, Link, MenuItem, MenuList, Stack, Theme, Tooltip, Typography } from "@mui/material";
import Enumerable from "linq";
import { useMemo, useRef, useState } from "react";
import { getObjectTypeName } from '../../common/utilities/GetObjectTypeName';
import useCustomizedNavigationRoutes from '../../hooks/use-customized-navigation-routes/UseCustomizedNavigationRoutes';
import { IRoute, UIViewSubCategory } from '../../routes/routes';
import { INavigationItem } from "../../services/navigation-data-service/Models";
import { IUICustomizationViewCategorySettingsDTO } from '../../services/ui-customization-data-service/Models';
import NavigationViewListUtils from "../navigation-view-list/NavigationViewListUtils";
import BreadCrumbMenu from "./BreadCrumbMenu";
import { UIViewEnum } from "../../common/enums/UIViewEnum";

export type BreadCrumbViewItemProps = {
    item: INavigationItem;
    route: IRoute;
    customizationSettings: IUICustomizationViewCategorySettingsDTO | undefined;
    onClick: (item: INavigationItem, route: IRoute, shiftKey: boolean, ctrlKey: boolean) => void;
};

export default function BreadCrumbViewItem({
    item,
    route,
    customizationSettings,
    onClick
}: BreadCrumbViewItemProps) {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [viewAllState, setViewAllState] = useState<boolean>(false);

    const isGrouped = !(customizationSettings?.ungrouped ?? false);
    const hasHiddenRoutes = customizationSettings?.views?.some(o => o.hidden) ?? false;

    const navigationRoutes = useCustomizedNavigationRoutes(
        item.objectTypeID,
        item.objectID,
        customizationSettings,
        viewAllState
    );

    const groupedNavigationRoutes = Enumerable.from(navigationRoutes)
        .where(o => o.parentViewID === undefined)
        .groupBy(
            o => isGrouped
                ? o.subCategory ?? UIViewSubCategory.Uncategorized
                : UIViewSubCategory.Uncategorized,
            o => o,
            (key, element) => ({
                subCategory: key,
                routes: element
                    .orderBy(o => o.order ?? 9999)
                    .toArray()
            }))
        .orderBy(o => o.subCategory ?? UIViewSubCategory.Uncategorized)
        .toArray();

    function handleOnClick(
        navItem: INavigationItem,
        navRoute: IRoute,
        shiftKey: boolean,
        ctrlKey: boolean
    ) {
        onClick(navItem, navRoute, shiftKey, ctrlKey);
        setOpen(false)
    }

    const displayRoute = useMemo(() => {

         if (route.viewID === UIViewEnum.Device_GaugeSheet &&
            !navigationRoutes.find(o => o.viewID === route.viewID)) {

                const manualEntryRoute = navigationRoutes.find(o => o.viewID === UIViewEnum.Device_ManualEntry);
                return manualEntryRoute ?? route;
        }

        return route;

    }, [
        route,
        navigationRoutes
    ]);
    
    return (
        <Stack
            direction="column"
            sx={{
                overflow: "hidden"
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden"
                }}
            >
                {`${getObjectTypeName(item.objectTypeID)} View`}
            </Typography>
            <Tooltip title={displayRoute.name}>
                <Stack
                    direction="row"
                    ref={anchorRef}
                    sx={{
                        alignItems: "center",
                        overflow: "hidden",
                        "& svg": {
                            marginTop: 'auto',
                            marginBottom: 'auto',
                            width: (theme: Theme) => theme.spacing(2),
                            height: (theme: Theme) => theme.spacing(2),
                        }
                    }}
                >
                    <Link
                        sx={{
                            color: "text.secondary",
                            display: "flex",
                            "&:hover": {
                                cursor: "pointer"
                            },
                            flexDirection: "row",
                            alignItems: "center",
                            overflow: "hidden"
                        }}
                        onClick={
                            event => handleOnClick(item, route, event.shiftKey, event.ctrlKey)
                        }
                    >
                        {displayRoute?.icon && <displayRoute.icon />}

                        <Box
                            sx={{
                                marginTop: 'auto',
                                marginBottom: 'auto',
                                marginLeft: 1,
                                color: "text.primary",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden"
                            }}
                        >
                            {displayRoute.name}
                        </Box>
                    </Link>

                    <IconButton
                        size="small"
                        aria-label="related"
                        aria-controls="related-items-menu"
                        aria-haspopup="true"
                        onClick={() => setOpen(prev => !prev)}
                        sx={{
                            color: "inherit"
                        }}
                    >
                        <ExpandMore sx={{ margin: 0 }} />
                    </IconButton>
                </Stack>
            </Tooltip>
            <BreadCrumbMenu
                anchorEl={anchorRef.current}
                open={open}
                onClose={() => setOpen(false)}
            >
                <Stack
                    direction="column"
                    sx={{
                        flex: "1 1 auto",
                        overflow: "hidden",
                    }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-end",
                            flex: "0 0 auto",
                            paddingLeft: 1,
                            paddingRight: 1,
                            paddingTop: 0.75,
                            paddingBottom: 0.75
                        }}
                    >
                        <IconButton
                            size="small"
                            disabled={!hasHiddenRoutes}
                            onClick={() => setViewAllState(prev => !prev)}
                        >
                            {viewAllState
                                ? <Tooltip arrow placement="top" title="Viewing Full Menu">
                                    <FontAwesomeIcon icon={faEye} />
                                </Tooltip>
                                : <Tooltip arrow placement="top" title="Viewing My Menu">
                                    <FontAwesomeIcon icon={faEyeLowVision} />
                                </Tooltip>
                            }
                        </IconButton>
                    </Stack>
                    <Divider />
                    <MenuList
                        sx={{
                            overflowY: "auto",
                            flex: "1 1 auto"
                        }}
                    >
                        {groupedNavigationRoutes
                            .filter(o => o.subCategory !== undefined && o.routes.length > 0)
                            .map((routeGroup, index) => [
                                <MenuItem
                                    key={`title:${index}`}
                                    disabled
                                    sx={{
                                        opacity: "1 !important"
                                    }}
                                >
                                    {isGrouped &&
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 700,
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                color: "text.secondary",
                                                padding: 0,
                                                margin: 0
                                            }}
                                        >
                                            {NavigationViewListUtils.getSubcategoryLabel(routeGroup.subCategory!)}
                                        </Typography>
                                    }
                                </MenuItem>
                                ,
                                ...routeGroup.routes.map((o, index) =>
                                    <MenuItem
                                        key={`item:${index}`}
                                        selected={o.viewID === route?.viewID}
                                        onClick={
                                            event => handleOnClick(item, o, event.shiftKey, event.ctrlKey)
                                        }
                                        sx={{
                                            // color: "red",
                                            // backgroundColor: "inherit",
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            sx={{
                                                alignItems: "center",
                                                width: 1,
                                                color: "inherit",
                                                backgroundColor: "inherit"
                                            }}>
                                            {<o.icon />}
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden"
                                                }}
                                            >
                                                {o.name}
                                            </Typography>
                                        </Stack>
                                    </MenuItem>
                                )
                            ])
                        }
                    </MenuList>
                </Stack>
            </BreadCrumbMenu>
        </Stack>
    );
}
